import {Dispatch, SetStateAction, useEffect} from 'react';

import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {getNodeConnectionCapabilities} from './procedureRunDesignFlowchartConnectionUtils';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutEdge} from './useProcedureRunDesignFlowchartEdges';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartConnectionCapabilitiesArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  removedNodeIds: ReadonlySet<string>;
  // Re-seeding signals: when the initial nodes are rebuilt (e.g. conditions/methods/status change),
  // the node store resets showPersistentSourceHandle to its static seed, so we must re-apply the live value.
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

/**
 * Applies edge-dependent source-handle visibility onto the grid nodes. Returns the same node object
 * when a node's `showPersistentSourceHandle` is unchanged, so re-applying the result can't trigger a layout
 * loop. `connectionCapabilities` are left untouched here: they are static and seeded once when nodes are built.
 */
export function applyShowPersistentSourceHandle(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  return nodes.map((node) => {
    if (!isProcedureRunDesignFlowchartNode(node)) {
      return node;
    }
    const next = showPersistentSourceHandleByNodeId.get(node.id) ?? false;
    return node.data.showPersistentSourceHandle === next
      ? node
      : {...node, data: {...node.data, showPersistentSourceHandle: next}};
  });
}

/**
 * Keeps each grid node's `showPersistentSourceHandle` in sync with the live edges, so drawing an
 * upstream->active edge recomputes handle visibility immediately. No-ops when a node's visibility is
 * unchanged, so it can't trigger a layout loop.
 */
export function useProcedureRunDesignFlowchartConnectionCapabilities({
  conditions,
  methods,
  activeMethodId,
  edges,
  removedNodeIds,
  initialColumnNodes,
  initialRows,
  setNodes,
}: UseProcedureRunDesignFlowchartConnectionCapabilitiesArgs): void {
  useEffect(() => {
    const {showPersistentSourceHandleByNodeId} = getNodeConnectionCapabilities({
      conditions,
      methods,
      edges,
      activeMethodId,
      removedNodeIds,
    });
    setNodes((current) => applyShowPersistentSourceHandle(current, showPersistentSourceHandleByNodeId));
  }, [conditions, methods, activeMethodId, edges, removedNodeIds, initialColumnNodes, initialRows, setNodes]);
}
